import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { createResourceHooks } from '@repo/api-client';
import { agentClient } from './network/agents.api';
import type { SuspendAgentRequest, AssignTerritoryRequest } from './types';

// The base hooks handle useList, useGet, useCreate, useUpdate, useRemove
export const agentHooks = {
  ...createResourceHooks(agentClient, ['agents']),

  useSuspend: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, body }: { id: string; body: SuspendAgentRequest }) =>
        agentClient.suspend(id, body),
      onSuccess: (_, { id }) => {
        qc.invalidateQueries({ queryKey: ['agents', 'list'] });
        qc.invalidateQueries({ queryKey: ['agents', 'detail', id] });
      },
    });
  },

  useTerminate: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => agentClient.terminate(id),
      onSuccess: (_, id) => {
        qc.invalidateQueries({ queryKey: ['agents', 'list'] });
        qc.invalidateQueries({ queryKey: ['agents', 'detail', id] });
      },
    });
  },

  useReinstate: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => agentClient.reinstate(id),
      onSuccess: (_, id) => {
        qc.invalidateQueries({ queryKey: ['agents', 'list'] });
        qc.invalidateQueries({ queryKey: ['agents', 'detail', id] });
      },
    });
  },

  useTerritory: (id: string) =>
    useQuery({
      queryKey: ['agents', 'territory', id],
      queryFn: () => agentClient.getTerritory(id),
      // Only enable if we have an ID
      enabled: !!id,
    }),

  useAssignTerritory: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, body }: { id: string; body: AssignTerritoryRequest }) =>
        agentClient.assignTerritory(id, body),
      onSuccess: (_, { id }) => {
        qc.invalidateQueries({ queryKey: ['agents', 'territory', id] });
      },
    });
  },

  useRemoveTerritory: () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: ({ id, orgUnitId }: { id: string; orgUnitId: string }) =>
        agentClient.removeTerritory(id, orgUnitId),
      onSuccess: (_, { id }) => {
        qc.invalidateQueries({ queryKey: ['agents', 'territory', id] });
      },
    });
  },

  usePerformance: (id: string) =>
    useQuery({
      queryKey: ['agents', 'performance', id],
      queryFn: () => agentClient.getPerformance(id),
      enabled: !!id,
    }),
};
